import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import { IconButton, Tooltip } from '@mui/material';
import { type FC, useEffect, useState } from 'react';

import { useToggleIrLight } from '../../api/index.js';
import useSnackBar from '../../hooks/use-snack-bar.js';

interface CameraIrToggleButtonProps {
  cameraId: string;
  irCutFilter?: boolean | null;
  irIlluminator?: boolean | null;
  /** Render for a dark video overlay: white icon, amber when on. */
  overlay?: boolean;
}

const CameraIrToggleButton: FC<CameraIrToggleButtonProps> = ({
  cameraId,
  irCutFilter,
  irIlluminator,
  overlay = false,
}) => {
  const snackBar = useSnackBar();
  const [on, setOn] = useState(irCutFilter ?? false);
  const [illuminator, setIlluminator] = useState<boolean | null | undefined>(irIlluminator);

  // Re-sync from persisted state when fresh props arrive (e.g. a query refetch
  // after the health-check cron updates the DB). Optimistic toggles still win
  // via onMutate while a mutation is in flight.
  useEffect(() => {
    if (irCutFilter != null) {
      setOn(irCutFilter);
    }
    setIlluminator(irIlluminator);
  }, [irCutFilter, irIlluminator]);

  const mutation = useToggleIrLight(cameraId, {
    onMutate: (enabled) => setOn(enabled),
    onSuccess: (data) => {
      const next = data.irCutFilter ?? on;
      setOn(next);
      setIlluminator(data.irIlluminator);
      snackBar.success(`IR light turned ${next ? 'on' : 'off'}.`);
    },
    onError: (_err, enabled) => {
      setOn(!enabled);
      snackBar.error('Failed to toggle IR light.');
    },
  });

  const illuminatorLabel =
    illuminator == null ? '' : ` · Illuminator ${illuminator ? 'on' : 'off'}`;
  const tooltipTitle = `Turn IR light ${on ? 'off' : 'on'}${illuminatorLabel}`;

  return (
    <Tooltip arrow placement="bottom" title={tooltipTitle}>
      <span>
        <IconButton
          aria-label="toggle IR light"
          color={overlay ? undefined : on ? 'primary' : 'default'}
          sx={overlay ? { color: on ? '#fac413' : 'white' } : undefined}
          disabled={mutation.isLoading}
          onClick={() => mutation.mutate(!on)}
        >
          <WbIncandescentIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default CameraIrToggleButton;
